'use client';
import { appConfig } from "@/_config"

const visionItems = [
    {
        icon: 'pi pi-home',
        title: 'A Seamless Lifestyle',
        desc: 'Envisioning homes that adapt intuitively to your needs, providing unparalleled convenience and comfort.',
    },
    {
        icon: 'pi pi-sun',
        title: 'Sustainability in Every Home',
        desc: 'Leveraging smart technology to optimize energy use, reduce waste, and promote eco-friendly living.',
    },
    {
        icon: 'pi pi-users',
        title: 'Accessible Innovation',
        desc: 'Making advanced automation affordable and user-friendly for all households.',
    },
    {
        icon: 'pi pi-shield',
        title: 'Enhanced Security & Peace of Mind',
        desc: 'Creating a safe, secure, and responsive environment with cutting-edge monitoring and control systems.',
    },
    {
        icon: 'pi pi-link',
        title: 'Integration for the Future',
        desc: 'Building ecosystems where devices communicate effortlessly, empowering a truly smart living experience.',
    },
];

const aboutItems = [
    {
        icon: 'pi pi-star',
        title: 'Who We Are',
        desc: 'A leading innovator in smart home solutions, dedicated to enhancing everyday living through cutting-edge automation technology.',
    },
    {
        icon: 'pi pi-flag',
        title: 'Our Mission',
        desc: 'To provide an integrated ecosystem that connects all major voice control platforms—Google Assistant, Siri (Apple Home Kit), Alexa, and more—into a single, unified Ennix device.',
    },
    {
        icon: 'pi pi-box',
        title: 'What We Offer',
        desc: 'Comprehensive solutions ranging from smart lighting and climate control to advanced security systems, all integrated into a seamless ecosystem.',
    },
    {
        icon: 'pi pi-heart',
        title: 'Why Choose Us',
        desc: 'Combining state-of-the-art technology, exceptional customer service, and a commitment to sustainability, we ensure a smarter and better living experience for our clients.',
    },
];

export const OurVisions = () => {
    return (
        <div style={{ background: '#f8faff' }}>
            <div id="aboutUs" className="h-5rem" />

            <div className="container" style={{ padding: '3rem 1.5rem 4rem' }}>

                {/* ── Section header ── */}
                <div className="flex flex-column align-items-center text-center mb-6">
                    <span style={{
                        background: 'rgba(11,49,99,0.08)',
                        color: 'var(--primary-color)',
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        letterSpacing: '1.5px',
                        textTransform: 'uppercase',
                        padding: '4px 16px',
                        borderRadius: 999,
                        marginBottom: '0.85rem',
                        display: 'inline-block',
                    }}>
                        About {appConfig.appShortName}
                    </span>
                    <h2 style={{ fontSize: 'clamp(1.6rem, 3vw, 2.2rem)', fontWeight: 800, color: 'var(--primary-color)', margin: 0 }}>
                        Building the Future of Smart Living
                    </h2>
                    <p style={{ marginTop: '0.75rem', color: 'var(--text-color-secondary)', maxWidth: '42rem', fontSize: '0.95rem' }}>
                        We bring cutting-edge home automation solutions that connect your world — intelligently, securely, and sustainably.
                    </p>
                </div>

                {/* ── Two-column layout ── */}
                <div className="grid" style={{ gap: 0 }}>

                    {/* ── Left: Vision ── */}
                    <div className="col-12 lg:col-6" style={{ paddingRight: '1.5rem' }}>
                        <div className="flex align-items-center gap-2 mb-4">
                            <div style={{
                                width: 4, height: 28, borderRadius: 999,
                                background: 'var(--primary-color)',
                                flexShrink: 0,
                            }} />
                            <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: 'var(--primary-color)' }}>
                                Our Vision
                            </h3>
                        </div>

                        <div className="flex flex-column gap-3">
                            {visionItems.map((v, i) => (
                                <div key={i} style={{
                                    display: 'flex',
                                    gap: '1rem',
                                    background: '#ffffff',
                                    border: '1.5px solid rgba(11,49,99,0.08)',
                                    borderRadius: 14,
                                    padding: '1rem 1.2rem',
                                    boxShadow: '0 2px 12px rgba(11,49,99,0.05)',
                                    transition: 'box-shadow 0.2s, transform 0.2s',
                                }}
                                    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 24px rgba(11,49,99,0.12)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 2px 12px rgba(11,49,99,0.05)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                                >
                                    <div style={{
                                        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                                        background: 'var(--primary-color)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <i className={v.icon} style={{ color: '#fff', fontSize: '1rem' }} />
                                    </div>
                                    <div>
                                        <p style={{ margin: '0 0 4px', fontWeight: 700, fontSize: '0.9rem', color: 'var(--primary-color)' }}>{v.title}</p>
                                        <p style={{ margin: 0, fontSize: '0.82rem', color: '#111827', lineHeight: 1.55 }}>{v.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ── Right: About ── */}
                    <div className="col-12 lg:col-6 mt-5 lg:mt-0" style={{ paddingLeft: '1.5rem' }}>
                        <div className="flex align-items-center gap-2 mb-4">
                            <div style={{
                                width: 4, height: 28, borderRadius: 999,
                                background: 'var(--secondary-color, #1bb8a7)',
                                flexShrink: 0,
                            }} />
                            <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: 'var(--primary-color)' }}>
                                About {appConfig.appShortName}
                            </h3>
                        </div>

                        <div className="flex flex-column gap-3">
                            {aboutItems.map((a, i) => (
                                <div key={i} style={{
                                    display: 'flex',
                                    gap: '1rem',
                                    background: '#ffffff',
                                    border: '1.5px solid rgba(27,184,167,0.15)',
                                    borderRadius: 14,
                                    padding: '1rem 1.2rem',
                                    boxShadow: '0 2px 12px rgba(27,184,167,0.06)',
                                    transition: 'box-shadow 0.2s, transform 0.2s',
                                }}
                                    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 24px rgba(27,184,167,0.18)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 2px 12px rgba(27,184,167,0.06)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                                >
                                    <div style={{
                                        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                                        background: 'var(--secondary-color, #1bb8a7)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <i className={a.icon} style={{ color: '#fff', fontSize: '1rem' }} />
                                    </div>
                                    <div>
                                        <p style={{ margin: '0 0 4px', fontWeight: 700, fontSize: '0.9rem', color: 'var(--primary-color)' }}>{a.title}</p>
                                        <p style={{ margin: 0, fontSize: '0.82rem', color: '#111827', lineHeight: 1.55 }}>{a.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ── Bottom stat bar ── */}
                <div className="grid mt-6" style={{
                    background: 'var(--primary-color)',
                    borderRadius: 16,
                    paddingTop: '2rem',
                    paddingBottom: '2rem',
                    margin: '2rem 0 0',
                }}>
                    {[
                        { icon: 'pi pi-check-circle', value: '500+', label: 'Happy Customers' },
                        { icon: 'pi pi-bolt',         value: '10K+', label: 'Devices Installed' },
                        { icon: 'pi pi-globe',        value: '5+',   label: 'Cities Covered' },
                        { icon: 'pi pi-star-fill',    value: '4.9★', label: 'Average Rating' },
                    ].map((s, i) => (
                        <div key={i} className="col-6 lg:col-3 flex flex-column align-items-center text-center gap-2">
                            <div style={{
                                width: 48, height: 48, borderRadius: '50%',
                                background: 'rgba(255,255,255,0.15)',
                                border: '1.5px solid rgba(255,255,255,0.25)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                            }}>
                                <i className={s.icon} style={{ color: '#fff', fontSize: '1.1rem' }} />
                            </div>
                            <p style={{ margin: 0, fontSize: '1.6rem', fontWeight: 800, color: '#ffffff', lineHeight: 1 }}>{s.value}</p>
                            <p style={{ margin: 0, fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)', fontWeight: 500 }}>{s.label}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
